/**
 * Builds the snake_case parameter bag used by remittance / remittance-item
 * GraphQL queries from the current user-applied filter state.
 *
 * Core has zero framework imports, so we do NOT depend on the presentation
 * `FilterInfo` type nor on `AppConfig`. Instead we declare a minimal structural
 * input that any caller can satisfy, and config-derived defaults are passed in
 * explicitly.
 */

export interface IDeductionFetchFilterOption {
  value: string | number;
  label?: string;
}

export interface IDeductionFetchFilterInput {
  invoiceFromDate: string | null;
  invoiceToDate: string | null;
  checkFromDate: string | null;
  checkToDate: string | null;
  purchaser: IDeductionFetchFilterOption[];
  dc: IDeductionFetchFilterOption[];
  priority: IDeductionFetchFilterOption[];
  validity: IDeductionFetchFilterOption[];
  adjustmentTypes: IDeductionFetchFilterOption[];
  adjustmentCategories: IDeductionFetchFilterOption[];
  deductionTypeCategories: IDeductionFetchFilterOption[];
  retailer: IDeductionFetchFilterOption[];
  analyst: IDeductionFetchFilterOption[];
  approver: IDeductionFetchFilterOption[];
  progress: IDeductionFetchFilterOption[];
  poNumberSearch: string | null;
}

export interface IBuildDeductionFetchParamsConfig {
  /** Default `check_from_date` when the user hasn't picked one (ISO date). */
  defaultCheckFromDate: string;
  /** Default `check_to_date` when the user hasn't picked one (ISO date). */
  defaultCheckToDate: string;
  /** When true, omit check date filters entirely unless the user set one. */
  isNoDefaultDateRange: boolean;
}

export interface IBuildDeductionFetchParamsInput {
  filterInfo: IDeductionFetchFilterInput;
  statusId: "all" | "incomplete" | number;
  search?: string | null;
  config: IBuildDeductionFetchParamsConfig;
  /** Include the global search + po_number_search fields. */
  includeSearchFields?: boolean;
  /** Include status_ids (only meaningful on the "all" tab). */
  includeStatusIds?: boolean;
}

export interface IDeductionFetchParams {
  search?: string;
  check_from_date?: string;
  check_to_date?: string;
  invoice_from_date?: string;
  invoice_to_date?: string;
  purchaser_ids?: number[];
  distribution_center_ids?: number[];
  priority_ids?: number[];
  validity_ids?: number[];
  adjustment_type_ids?: number[];
  adjustment_category_ids?: number[];
  deduction_type_category_ids?: number[];
  retailer_ids?: number[];
  analyst_ids?: number[];
  approver_ids?: number[];
  status_ids?: number[];
  po_number_search?: string;
}

const mapOptionsToIds = (options: IDeductionFetchFilterOption[] | undefined): number[] | undefined => {
  if (!options || options.length === 0) return undefined;
  return options.map(option => Number(option.value));
};

const resolveCheckDate = (
  date: string | null | undefined,
  isNoDefaultDateRange: boolean,
  fallback: string
): string | undefined => {
  if (isNoDefaultDateRange && !date) return undefined;
  return date ?? fallback;
};

export class BuildDeductionFetchParamsUseCase {
  execute(input: IBuildDeductionFetchParamsInput): IDeductionFetchParams {
    const { filterInfo, statusId, search, config, includeSearchFields, includeStatusIds } = input;

    const params: IDeductionFetchParams = {
      check_from_date: resolveCheckDate(
        filterInfo.checkFromDate,
        config.isNoDefaultDateRange,
        config.defaultCheckFromDate
      ),
      check_to_date: resolveCheckDate(
        filterInfo.checkToDate,
        config.isNoDefaultDateRange,
        config.defaultCheckToDate
      ),
      invoice_from_date: filterInfo.invoiceFromDate ?? undefined,
      invoice_to_date: filterInfo.invoiceToDate ?? undefined,
      purchaser_ids: mapOptionsToIds(filterInfo.purchaser),
      distribution_center_ids: mapOptionsToIds(filterInfo.dc),
      priority_ids: mapOptionsToIds(filterInfo.priority),
      validity_ids: mapOptionsToIds(filterInfo.validity),
      adjustment_type_ids: mapOptionsToIds(filterInfo.adjustmentTypes),
      adjustment_category_ids: mapOptionsToIds(filterInfo.adjustmentCategories),
      deduction_type_category_ids: mapOptionsToIds(filterInfo.deductionTypeCategories),
      retailer_ids: mapOptionsToIds(filterInfo.retailer),
      analyst_ids: mapOptionsToIds(filterInfo.analyst),
      approver_ids: mapOptionsToIds(filterInfo.approver),
    };

    if (includeSearchFields) {
      params.search = search ?? undefined;
      params.po_number_search = filterInfo.poNumberSearch ?? undefined;
    }

    if (includeStatusIds) {
      params.status_ids = statusId === "all" ? mapOptionsToIds(filterInfo.progress) : undefined;
    }

    return params;
  }
}
